import {Async, Empty} from 'Core/models/Async';
import {QuoteListAction} from 'Quoting/components/Opportunity/QuoteActions/QuoteActionUtils';
import {SelectedQuoteDetails} from 'Quoting/components/Opportunity/QuotesTab';
import {
  DecisionHeapedTable,
  DecisionLevelTable,
  EvaluateHeapedPayload,
  EvaluateLevelPayload,
} from 'Quoting/models/DTOs/CompensationPayload';
import {ProposalQuoteDetails, ProposalResponseDTO} from 'Quoting/models/DTOs/Proposal';
import { QuoteStatus } from 'Quoting/models/enum';
import {createAction} from 'Shared/utilities/actionCreator';

export enum QuoteActionType {
  PREPARE_FOR_PROPOSAL = 'quoteAction/PREPARE_FOR_PROPOSAL',
  CREATE_PROPOSAL = 'quoteAction/CREATE_PROPOSAL',
  GENERATE_PROPOSAL = 'quoteAction/GENERATE_PROPOSAL',
  DECLINE_QUOTE = 'quoteAction/DECLINE_QUOTE',
  COPY_QUOTE = 'quoteAction/COPY_QUOTE',
  UPDATE_PROPOSAL_SELECTED_FOR_GENEATION = 'quoteAction/UPDATE_PROPOSAL_SELECTED_FOR_GENEATION',
  UPDATE_SELECTED_QUOTES_FOR_ACTION = 'quoteAction/UPDATE_SELECTED_QUOTES_FOR_ACTION',
  SET_SINGLE_SELECTED_QUOTE_FOR_ACTION = 'quoteAction/SET_SINGLE_SELECTED_QUOTE_FOR_ACTION',
  UPDATE_QUOTES_FOR_SELECTED_PROPOSAL = 'quoteAction/UPDATE_QUOTES_FOR_SELECTED_PROPOSAL',
  RETRIEVE_QUOTES_FOR_GENERATE_PROPOSAL = 'quoteAction/RETRIEVE_QUOTES_FOR_GENERATE_PROPOSAL',
  TOGGLE_QUOTES_TAB_ACTION_MODAL = 'quoteAction/TOGGLE_QUOTES_TAB_ACTION_MODAL',
  TOGGLE_QUOTES_TAB_ACTION_CONFIRMATION_MODAL = 'quoteAction/TOGGLE_QUOTES_TAB_ACTION_CONFIRMATION_MODAL',
  UPDATE_SELECTED_QUOTE_ACTION = 'quoteAction/UPDATE_SELECTED_QUOTE_ACTION',
  UPDATE_QUOTE_DECLINE_DETAILS = 'quoteAction/UPDATE_QUOTE_DECLINE_DETAILS',
  CLEAR_QUOTE_DECLINE_DETAILS = 'quoteAction/CLEAR_QUOTE_DECLINE_DETAILS',
  CLEAR_SELECTED_QUOTES_FOR_ACTION = 'quoteAction/CLEAR_SELECTED_QUOTES_FOR_ACTION',
  APPLY_QUOTE_LIST_FILTER = 'quoteAction/APPLY_QUOTE_LIST_FILTER',
  EVALUATE_DECISION_TABLE = 'quoteAction/EVALUATE_DECISION_TABLE',
  UPDATE_DECISION_TABLE_LEVEL_RESULTS = 'quoteAction/UPDATE_DECISION_TABLE_LEVEL_RESULTS',
  UPDATE_DECISION_TABLE_HEAPED_RESULTS = 'quoteAction/UPDATE_DECISION_TABLE_HEAPED_RESULTS',
  UPDATE_COPY_QUOTE_DETAILS = 'quoteAction/COPY_QUOTE_DETAILS',
  CLEAR_COPY_QUOTE_DETAILS = 'quoteAction/CLEAR_COPY_QUOTE_DETAILS',
  CLEAR_COMPENSATION_DECISION_TABLE_RESPONSE = 'quoteAction/CLEAR_COMPENSATION_DECISION_TABLE_RESPONSE',
  SET_QUOTE_ACTION_SOURCE = 'quoteAction/SET_QUOTE_ACTION_SOURCE',
  UPDATE_SINGLE_QUOTE_SELECTED_FOR_ACTION = 'quoteAction/UPDATE_SINGLE_QUOTE_SELECTED_FOR_ACTION',
}

export type QuoteDeclineDetails = {
  declineBy: 'customer' | 'company';
  requestPayload: {
    declineDate: string;
    declineOtherReason: string;
    declineReason: string;
  };
};

export interface UriDTO {
  _uri: string;
}

export const enum QuoteActionSource {
  QuoteList = 'QuoteList',
  ActionBtn = 'ActionBtn',
}

export type CopyQuoteDetails = {
  requestPayload: {
    txEffectiveDate: string;
    customer: UriDTO;
    opportunity: UriDTO;
  };
};

export const prepareForProposal = (
  selectedQuotes: SelectedQuoteDetails[],
  opportunityRootId: string,
  opportunityRevisionNo: number
) => createAction(QuoteActionType.PREPARE_FOR_PROPOSAL, {selectedQuotes, opportunityRootId, opportunityRevisionNo});

export const createProposal = (
  opportunityNumber: string,
  opportunityRootId: string,
  customerNumber: string,
  selectedQuotes: SelectedQuoteDetails[],
  newProposalData: {
    name: string;
    proposalExpirationDate: string;
  }
) =>
  createAction(QuoteActionType.CREATE_PROPOSAL, {
    opportunityNumber,
    opportunityRootId,
    customerNumber,
    selectedQuotes,
    newProposalData,
  });

export const generateProposal = (proposalRootId: string, proposalRevisionNo: number, opportunityId: string) =>
  createAction(QuoteActionType.GENERATE_PROPOSAL, {proposalRootId, proposalRevisionNo, opportunityId});

export const declineQuote = (
  quoteDeclineDetails: QuoteDeclineDetails,
  selectedQuotes: SelectedQuoteDetails[],
  opportunityRootId: string,
  opportunityRevisionNo: number
) =>
  createAction(QuoteActionType.DECLINE_QUOTE, {
    quoteDeclineDetails,
    selectedQuotes,
    opportunityRootId,
    opportunityRevisionNo,
  });

export const copyQuote = (
  copyQuoteDetails: CopyQuoteDetails,
  selectedQuotes: SelectedQuoteDetails[],
  opportunityRootId: string,
  opportunityRevisionNo: number
) =>
  createAction(QuoteActionType.COPY_QUOTE, {
    copyQuoteDetails,
    selectedQuotes,
    opportunityRootId,
    opportunityRevisionNo,
  });

export const updateQuotesSelectedForAction = (quoteDetails: SelectedQuoteDetails) =>
  createAction(QuoteActionType.UPDATE_SELECTED_QUOTES_FOR_ACTION, {quoteDetails});

export const updateSingleQuoteSelectedForAction = (quoteDetails: SelectedQuoteDetails) =>
  createAction(QuoteActionType.UPDATE_SINGLE_QUOTE_SELECTED_FOR_ACTION, {quoteDetails});

export const setSingleQuoteForAction = (quoteDetails: SelectedQuoteDetails) =>
  createAction(QuoteActionType.SET_SINGLE_SELECTED_QUOTE_FOR_ACTION, {quoteDetails});

export const updateProposalSelectedForGeneration = (proposalToGenerate: Async<ProposalResponseDTO>) =>
  createAction(QuoteActionType.UPDATE_PROPOSAL_SELECTED_FOR_GENEATION, {proposalToGenerate});

export const udpateQuotesForSelectedProposal = (quotes: ProposalQuoteDetails[]) =>
  createAction(QuoteActionType.UPDATE_QUOTES_FOR_SELECTED_PROPOSAL, {quotes});

export const retrieveQuotesForGenerateProposal = (quoteLinks: string[]) =>
  createAction(QuoteActionType.RETRIEVE_QUOTES_FOR_GENERATE_PROPOSAL, {quoteLinks});

export const toggleQuotesTabActionModal = (state: boolean) =>
  createAction(QuoteActionType.TOGGLE_QUOTES_TAB_ACTION_MODAL, {state});

export const toggleQuoteActionConfirmationModal = (state: boolean) =>
  createAction(QuoteActionType.TOGGLE_QUOTES_TAB_ACTION_CONFIRMATION_MODAL, {state});

export const updateSelectedQuoteAction = (action: QuoteListAction) =>
  createAction(QuoteActionType.UPDATE_SELECTED_QUOTE_ACTION, {action});

export const updateQuoteDeclineDetails = (quoteDeclineDetails: QuoteDeclineDetails) =>
  createAction(QuoteActionType.UPDATE_QUOTE_DECLINE_DETAILS, {quoteDeclineDetails});

export const updateCopyQuoteDetails = (copyQuoteDetails: CopyQuoteDetails) =>
  createAction(QuoteActionType.UPDATE_COPY_QUOTE_DETAILS, {copyQuoteDetails});

export const clearCompensationDecisionTableResponse = () =>
  createAction(QuoteActionType.CLEAR_COMPENSATION_DECISION_TABLE_RESPONSE);

export const clearQuoteDeclineDetails = () => createAction(QuoteActionType.CLEAR_QUOTE_DECLINE_DETAILS);

export const clearSelectedQuotesForAction = () => createAction(QuoteActionType.CLEAR_SELECTED_QUOTES_FOR_ACTION);

export const applyQuoteListFilter = (quoteListFilter: QuoteStatus) => createAction(QuoteActionType.APPLY_QUOTE_LIST_FILTER, {quoteListFilter});

export const setQuoteActionSource = (source: QuoteActionSource) =>
  createAction(QuoteActionType.SET_QUOTE_ACTION_SOURCE, {source});

export const clearCopyQuoteDetails = () => createAction(QuoteActionType.CLEAR_COPY_QUOTE_DETAILS);

export const evaluateDecisionTable = (
  payloadLevel: EvaluateLevelPayload,
  payloadHeaped: EvaluateHeapedPayload,
  productCd: string,
  onComplete?: (response: {levelEqu: string, heapedValues: DecisionHeapedTable[]}) => void
) => createAction(QuoteActionType.EVALUATE_DECISION_TABLE, {payloadLevel, payloadHeaped, productCd, onComplete});

export const updateDecisionTableLevelResults = (levelResult: DecisionLevelTable[]) =>
  createAction(QuoteActionType.UPDATE_DECISION_TABLE_LEVEL_RESULTS, {levelResult});

export const updateDecisionTableHeapedResults = (heapedResult: DecisionHeapedTable[]) =>
  createAction(QuoteActionType.UPDATE_DECISION_TABLE_HEAPED_RESULTS, {heapedResult});

type QuoteAction =
  | ReturnType<typeof prepareForProposal>
  | ReturnType<typeof createProposal>
  | ReturnType<typeof generateProposal>
  | ReturnType<typeof declineQuote>
  | ReturnType<typeof updateProposalSelectedForGeneration>
  | ReturnType<typeof updateQuotesSelectedForAction>
  | ReturnType<typeof setSingleQuoteForAction>
  | ReturnType<typeof toggleQuotesTabActionModal>
  | ReturnType<typeof updateQuoteDeclineDetails>
  | ReturnType<typeof updateSelectedQuoteAction>
  | ReturnType<typeof toggleQuoteActionConfirmationModal>
  | ReturnType<typeof clearQuoteDeclineDetails>
  | ReturnType<typeof evaluateDecisionTable>
  | ReturnType<typeof updateDecisionTableLevelResults>
  | ReturnType<typeof updateDecisionTableHeapedResults>
  | ReturnType<typeof udpateQuotesForSelectedProposal>
  | ReturnType<typeof updateCopyQuoteDetails>
  | ReturnType<typeof clearCopyQuoteDetails>
  | ReturnType<typeof clearSelectedQuotesForAction>
  | ReturnType<typeof applyQuoteListFilter>
  | ReturnType<typeof clearCompensationDecisionTableResponse>
  | ReturnType<typeof setQuoteActionSource>
  | ReturnType<typeof updateSingleQuoteSelectedForAction>;

type QuoteActionState = {
  proposalToGenerate: Async<ProposalResponseDTO>;
  selectedQuotesForAction: Record<string, SelectedQuoteDetails>; // multiple quotes selected via checkbox for action via Action Button
  selectedSingleQuoteForAction: SelectedQuoteDetails | null; // single quote selected from quoteList via Ellipse Icon on Quote Card
  quotesForSelectedProposal: ProposalQuoteDetails[];
  isQuoteActionModalOpen: boolean;
  isQuoteActionConfirmationModalOpen: boolean;
  selectedQuoteAction?: QuoteListAction;
  quoteDeclineDetails?: QuoteDeclineDetails;
  decisionTableLevelResponse: DecisionLevelTable[];
  decisionTableHeapedResponse: DecisionHeapedTable[];
  copyQuoteDetails?: CopyQuoteDetails;
  actionSource?: QuoteActionSource;
  quoteListFilter: QuoteStatus;
};

const DEFAULT_QUOTE_ACTION_STATE: QuoteActionState = {
  proposalToGenerate: new Empty(),
  selectedQuotesForAction: {},
  selectedSingleQuoteForAction: null,
  quotesForSelectedProposal: [],
  isQuoteActionModalOpen: false,
  isQuoteActionConfirmationModalOpen: false,
  decisionTableLevelResponse: [],
  decisionTableHeapedResponse: [],
  actionSource: QuoteActionSource.QuoteList,
  quoteListFilter: QuoteStatus.AllStatus,
};

export default (state: QuoteActionState = DEFAULT_QUOTE_ACTION_STATE, action: QuoteAction): QuoteActionState => {
  switch (action.type) {
    case QuoteActionType.UPDATE_SELECTED_QUOTES_FOR_ACTION: {
      const isExistingQuote = state.selectedQuotesForAction[action.payload.quoteDetails.rootId];
      if (isExistingQuote) {
        const {
          [action.payload.quoteDetails.rootId]: removedItem,
          ...otherSelectedQuotes
        } = state.selectedQuotesForAction;
        return {...state, selectedQuotesForAction: otherSelectedQuotes};
      }
      return {
        ...state,
        selectedQuotesForAction: {
          ...state.selectedQuotesForAction,
          [action.payload.quoteDetails.rootId]: action.payload.quoteDetails,
        },
      };
    }

    case QuoteActionType.UPDATE_PROPOSAL_SELECTED_FOR_GENEATION: {
      return {
        ...state,
        proposalToGenerate: action.payload.proposalToGenerate,
      };
    }

    case QuoteActionType.UPDATE_QUOTES_FOR_SELECTED_PROPOSAL: {
      return {
        ...state,
        quotesForSelectedProposal: action.payload.quotes,
      };
    }

    case QuoteActionType.TOGGLE_QUOTES_TAB_ACTION_MODAL: {
      return {
        ...state,
        isQuoteActionModalOpen: action.payload.state,
      };
    }

    case QuoteActionType.CLEAR_QUOTE_DECLINE_DETAILS: {
      return {
        ...state,
        quoteDeclineDetails: undefined,
      };
    }

    case QuoteActionType.CLEAR_SELECTED_QUOTES_FOR_ACTION: {
      return {
        ...state,
        selectedQuotesForAction: {},
        selectedSingleQuoteForAction: null,
      };
    }

    case QuoteActionType.APPLY_QUOTE_LIST_FILTER: {
      return {
        ...state,
        quoteListFilter: action.payload.quoteListFilter,
      };
    }

    case QuoteActionType.TOGGLE_QUOTES_TAB_ACTION_CONFIRMATION_MODAL: {
      return {
        ...state,
        isQuoteActionConfirmationModalOpen: action.payload.state,
      };
    }

    case QuoteActionType.UPDATE_SELECTED_QUOTE_ACTION: {
      return {
        ...state,
        selectedQuoteAction: action.payload.action,
      };
    }

    case QuoteActionType.UPDATE_QUOTE_DECLINE_DETAILS: {
      return {
        ...state,
        quoteDeclineDetails: action.payload.quoteDeclineDetails,
      };
    }

    case QuoteActionType.UPDATE_COPY_QUOTE_DETAILS: {
      return {
        ...state,
        copyQuoteDetails: action.payload.copyQuoteDetails,
      };
    }

    case QuoteActionType.CLEAR_COPY_QUOTE_DETAILS: {
      return {
        ...state,
        copyQuoteDetails: undefined,
      };
    }

    case QuoteActionType.UPDATE_DECISION_TABLE_LEVEL_RESULTS: {
      return {
        ...state,
        decisionTableLevelResponse: action.payload.levelResult,
      };
    }

    case QuoteActionType.UPDATE_DECISION_TABLE_HEAPED_RESULTS: {
      return {
        ...state,
        decisionTableHeapedResponse: action.payload.heapedResult,
      };
    }

    case QuoteActionType.CLEAR_COMPENSATION_DECISION_TABLE_RESPONSE: {
      return {
        ...state,
        decisionTableHeapedResponse: [],
        decisionTableLevelResponse: [],
      };
    }

    case QuoteActionType.SET_QUOTE_ACTION_SOURCE: {
      return {
        ...state,
        actionSource: action.payload.source,
      };
    }

    case QuoteActionType.UPDATE_SINGLE_QUOTE_SELECTED_FOR_ACTION: {
      return {
        ...state,
        selectedSingleQuoteForAction: action.payload.quoteDetails,
      };
    }

    default:
      return state;
  }
};
